import React, { useEffect, useMemo, useState } from "react";
import styles from "./Durations.module.scss";
import ProviderRows from "./DurationCrypto/DurationCrypto";
import { CatalogItem, fetchPoolsCatalog, groupCatalogItems, readPoolsCatalogCache } from "../../pages/Pools/catalogFacade";
import { fetchCreditMarkets, percent } from "../../pages/Credits/creditMarkets";

type Section = "Staking" | "Lending" | "Credit pools" | "Spot" | "Derivatives" | "Tokenized assets";

const sectionLabels: Record<Section, string> = {
  Staking: "Staking",
  Lending: "Lending",
  "Credit pools": "Credit pools",
  Spot: "Spot",
  Derivatives: "Derivatives",
  "Tokenized assets": "Tokenized assets",
};

const sectionCopy: Record<Section, { title: string; description: string; search: string }> = {
  Staking: {
    title: "STAKING",
    description: "Explore staking products and validator options from connected protocols.",
    search: "Search staking assets or validators",
  },
  Lending: {
    title: "LENDING",
    description: "Explore assets available for lending at variable provider rates.",
    search: "Search lending assets",
  },
  "Credit pools": {
    title: "CREDIT POOLS",
    description: "Supply-side liquidity markets that fund crypto-backed credit through Morpho on Base.",
    search: "Search loan or collateral assets",
  },
  Spot: {
    title: "SPOT MARKETS",
    description: "Explore supported assets and available market prices.",
    search: "Search spot assets",
  },
  Derivatives: {
    title: "DERIVATIVES",
    description: "High-risk perpetual markets. Eligibility and wallet signing are not enabled.",
    search: "Search perpetual markets",
  },
  "Tokenized assets": {
    title: "TOKENIZED ASSETS",
    description: "Explore tokenized stocks, ETFs and other real-world assets.",
    search: "Search tokenized assets",
  },
};

const sectionMatches = (section: Section, category: string) =>
  category === section;

const allSections: Section[] = ["Staking", "Lending", "Credit pools", "Spot", "Derivatives", "Tokenized assets"];

const Durations = () => {
  const [section, setSection] = useState<Section>("Staking");
  const [search, setSearch] = useState("");
  const [network, setNetwork] = useState("All networks");
  const [provider, setProvider] = useState("All providers");
  const [catalog, setCatalog] = useState<CatalogItem[]>(() => {
    const cached = readPoolsCatalogCache();
    return cached ? groupCatalogItems(cached.items) : [];
  });
  const [creditPools, setCreditPools] = useState<CatalogItem[]>([]);
  const [catalogError, setCatalogError] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(() => !readPoolsCatalogCache());
  const [reloadKey, setReloadKey] = useState(0);
  const copy = sectionCopy[section];
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setCatalogLoading(catalog.length === 0);
    setCatalogError("");
    fetchPoolsCatalog(controller.signal)
      .then((catalogResult) => {
        if (active) {
          setCatalog(groupCatalogItems(catalogResult.items));
          setSearch("");
          setNetwork("All networks");
          setProvider("All providers");
        }
      })
      .finally(() => {
        if (active) setCatalogLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [reloadKey]);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    fetchCreditMarkets(controller.signal).then((result) => {
      if (!active) return;
      setCreditPools(result.markets.filter((market) => market.supplyEnabled).map((market) => ({
        id: `morpho:${market.marketId}`,
        category: "Credit pools",
        asset: `${market.loanAsset.symbol} credit pool`,
        symbol: `${market.loanAsset.symbol} · ${market.collateralAsset.symbol} collateral`,
        icons: [market.loanAsset.symbol.toLowerCase()],
        provider: "Morpho",
        network: "Base",
        rate: percent(market.supplyApy),
        rate_percent: String(market.supplyApy * 100),
        lock: `Variable · ${Math.min(market.utilization * 100, 999).toFixed(1)}% utilized`,
        risks: `Variable supply APY. Borrower collateral: ${market.collateralAsset.symbol}. Liquidity and withdrawals follow the selected Morpho market's onchain state.`,
        source: "Morpho live market data",
        enabled: false,
        offers: [{ provider: "Morpho", network: "Base", rate: percent(market.supplyApy), rate_percent: String(market.supplyApy * 100), terms: `${market.collateralAsset.symbol} collateral · ${market.lltvPercent.toFixed(2)}% LLTV` }],
      })));
    }).catch(() => {});
    return () => { active = false; controller.abort(); };
  }, [reloadKey]);
  const allCatalog = useMemo(() => [...catalog, ...creditPools], [catalog, creditPools]);
  const availableSections = useMemo(
    () => allSections.filter((candidate) => allCatalog.some((item) => sectionMatches(candidate, item.category))),
    [allCatalog]
  );
  useEffect(() => {
    if (!catalogLoading && availableSections.length > 0 && !availableSections.includes(section)) {
      selectSection(availableSections[0]);
    }
  }, [availableSections, catalogLoading, section]);
  const sectionItems = useMemo(
    () => allCatalog.filter((item) => sectionMatches(section, item.category)),
    [allCatalog, section]
  );
  const networks = useMemo(
    () => ["All networks", ...Array.from(new Set(sectionItems.map((item) => item.network)))],
    [sectionItems]
  );
  const providers = useMemo(
    () => ["All providers", ...Array.from(new Set(sectionItems.map((item) => item.provider)))],
    [sectionItems]
  );
  const filteredItems = useMemo(
    () =>
      sectionItems.filter(
        (item) =>
          (network === "All networks" || item.network === network) &&
          (provider === "All providers" || item.provider === provider) &&
          `${item.asset} ${item.symbol} ${item.provider} ${item.network}`
            .toLowerCase()
            .includes(search.toLowerCase())
      ),
    [network, provider, search, sectionItems]
  );
  const items = filteredItems;

  const selectSection = (nextSection: Section) => {
    setSection(nextSection);
    setSearch("");
    setNetwork("All networks");
    setProvider("All providers");
  };

  return (
    <div className={styles.durations__wrapper}>
      <div className={styles.durations__wrapperHeader}>
        <div className={styles.durations__title}>Pools &amp; Earn</div>
        <div className={styles.categoryNav}>
          <div className={styles.categoryNavLabel}>Products</div>
          <div className={styles.categoryTabs} role="tablist" aria-label="Pools and earn sections">
          {availableSections.map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={section === item}
              className={`${styles.categoryTab} ${section === item ? styles.categoryTabActive : ""}`}
              onClick={() => selectSection(item)}
            >
              <span>{sectionLabels[item]}</span>
            </button>
          ))}
          </div>
        </div>
      </div>

      <div className={styles.durations__name}>
        {copy.title}
        <div className={styles.durations__nameTtile}>{copy.description}</div>
      </div>

      <div className={styles.assetSearchRow}>
        <label className={styles.searchField}>
          <img src="/image/durationSearch.svg" alt="" aria-hidden="true" />
          <input
            className={styles.durations__searchInput}
            placeholder={copy.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search assets"
          />
        </label>
        <select className={styles.assetFilter} value={network} onChange={(event) => setNetwork(event.target.value)} aria-label="Filter by network">
          {networks.map((item) => <option key={item}>{item}</option>)}
        </select>
        <select className={styles.assetFilter} value={provider} onChange={(event) => setProvider(event.target.value)} aria-label="Filter by provider">
          {providers.map((item) => <option key={item}>{item}</option>)}
        </select>
      </div>
      <div className={styles.assetCount}>{filteredItems.length} {filteredItems.length === 1 ? "asset" : "assets"}</div>

      {catalogLoading ? (
        <div className={styles.emptyState}>Loading provider data…</div>
      ) : catalogError ? (
        <div className={styles.emptyState}>
          {catalogError} <button type="button" onClick={() => setReloadKey((value) => value + 1)}>Retry</button>
        </div>
      ) : filteredItems.length > 0 ? (
        <ProviderRows items={items} category={section} />
      ) : (
        <div className={styles.emptyState}>No assets match the current search or filters.</div>
      )}
    </div>
  );
};

export default Durations;
